import { DateUtil, DateRange, DisplayType, Model, ModelRow, LogManager } from "@mcleod/core";
import { AutogenLayoutRenditionMonitor } from "./autogen/AutogenLayoutRenditionMonitor";
import { ClickEvent,  DataSourceExecutionEvent } from "@mcleod/components";
import { ImgLic } from "./ImgLic";

let renlgxLicensed: boolean = false;

const log = LogManager.getLogger("imaging.RenditionMonitor");

export class RenditionMonitor extends AutogenLayoutRenditionMonitor {
    private timer: any;
    private jobNumber: number;
    private icnt: number;

    override async onLoad() {
        renlgxLicensed = await ImgLic.renlgxLicensed();
        this.layoutLGX.visible = renlgxLicensed;
        this.switchShowLGX.visible = renlgxLicensed;
        (this.layoutPrint as any).labelHeading.text = "Print";
        (this.layoutEmail as any).labelHeading.text = "Email";
        (this.layoutFax as any).labelHeading.text = "Fax";
        (this.layoutFTP as any).labelHeading.text = "FTP";
        (this.layoutLGX as any).labelHeading.text = "LOGIX";

        this.sourceRenditionBilling.search({ renb_done: "N" }); //selects all unprocessed renbill records
    }

    /** This is an event handler for the onClick event of buttonLoadJob. */
    async buttonLoadJobOnClick(event: ClickEvent) {
        if (this.timer == null) {
            this.jobNumber = parseInt(this.textboxJobNumber.text);
            if (isNaN(this.jobNumber)) {
                this.textboxJobNumber.validationWarning = "Please enter a valid job number.";
            } else {
                this.startMonitor();
            }
        } else {
            this.stopMonitor();
        }
    }

    public showJob(jobNumber: number) {
        this.jobNumber = jobNumber;
        this.textboxJobNumber.text = this.jobNumber.toString();
        this.textboxJobNumber.displayType = DisplayType.STRING;
        this.textboxJobNumber.printable = true;
        this.startMonitor();
    }

    private async loadData() {
        const result = await Model.search("imaging/rendition-monitor", { rensp_jobno: this.jobNumber });
        this.hideNonApplicableComponents(result.modelRows);
        this.displayRows(result.modelRows);
        if (this.isJobFinished(result.modelRows)) {
            this.stopMonitor();
        }
    }

    private startMonitor() {
        this.icnt = 0;
        this.loadData();
        clearInterval(this.timer);
        this.buttonLoadJob.caption = "Stop monitoring";
        this.timer = setInterval(() => this.loadData(), 5000);
    }

    private stopMonitor() {
        clearInterval(this.timer);
        this.buttonLoadJob.caption = "Monitor";
        this.timer = null;
    }

    private displayRows(rows: ModelRow[]) {
        const targetLayouts = {
            P: this.layoutPrint,
            E: this.layoutEmail,
            F: this.layoutFax,
            T: this.layoutFTP,
            X: this.layoutLGX
        };
        this.zeroProgress(this.layoutTotal);
        Object.values(targetLayouts).forEach(targetLayout => this.zeroProgress(targetLayout));
        for (const row of rows) {
            const targetLayout = targetLayouts[row.get("rensp_sendto")];
            if( targetLayout )
                this.incrementProgress(targetLayout, row);
            this.incrementProgress(this.layoutTotal, row);
        }
    }

    private zeroProgress(targetLayout: any) {
        targetLayout.progressbarFoldersSpooled.value = 0;
        targetLayout.progressbarFoldersSpooled.maxValue = 0;
        targetLayout.progressbarPagesSpooled.value = 0;
        targetLayout.progressbarPagesSpooled.maxValue = 0;
        targetLayout.progressbarFoldersProcessed.value = 0;
        targetLayout.progressbarFoldersProcessed.maxValue = 0;
        targetLayout.progressbarPagesProcessed.value = 0;
        targetLayout.progressbarPagesProcessed.maxValue = 0;
    }

    private incrementProgress(targetLayout: any, row: ModelRow) {
        targetLayout.progressbarFoldersSpooled.value += row.get("rensp_fspooled");
        targetLayout.progressbarFoldersSpooled.maxValue += row.get("rensp_folders");
        targetLayout.progressbarPagesSpooled.value += row.get("rensp_pspooled");
        targetLayout.progressbarPagesSpooled.maxValue += row.get("rensp_pages");
        targetLayout.progressbarFoldersProcessed.value += row.get("rensd_fsent");
        targetLayout.progressbarFoldersProcessed.maxValue += row.get("rensd_folders");
        targetLayout.progressbarPagesProcessed.value += row.get("rensd_psent");
        targetLayout.progressbarPagesProcessed.maxValue += row.get("rensd_pages");
    }

    private hideNonApplicableComponents(rows: ModelRow[]) {
        const sendTos = [];
        rows.forEach(row => sendTos.push(row.get("rensp_sendto")));
        this.switchShowPrint.visible = this.switchShowPrint.checked = sendTos.includes("P");
        this.switchShowEmail.visible = this.switchShowEmail.checked = sendTos.includes("E");
        this.switchShowFax.visible = this.switchShowFax.checked = sendTos.includes("F"); // I'm not sure which sendTo value is for fax= vs. FTP
        this.switchShowFTP.visible = this.switchShowFTP.checked = sendTos.includes("T");
        if( renlgxLicensed )
            this.switchShowLGX.visible = this.switchShowLGX.checked = sendTos.includes("X");
    }


    private isJobFinished(rows: ModelRow[]): boolean {
        let isFinished = false;
        const tcnt = rows.length*4;
        let cnt=0;
        const targetLayouts = {
            P: this.layoutPrint,
            E: this.layoutEmail,
            F: this.layoutFax,
            T: this.layoutFTP,
            X: this.layoutLGX
        };
        for (const row of rows) {
            const targetLayout = targetLayouts[row.get("rensp_sendto")];
            if( targetLayout )
            {
                if( targetLayout == this.layoutEmail )
                    cnt++;      //added to folders cnt for email due to cover page count, may need to be fixed
                if( targetLayout.progressbarFoldersSpooled.value == targetLayout.progressbarFoldersSpooled.maxValue )
                    cnt++;
                if( targetLayout.progressbarPagesSpooled.value == targetLayout.progressbarPagesSpooled.maxValue )
                    cnt++;
                if( targetLayout.progressbarFoldersProcessed.value == targetLayout.progressbarFoldersProcessed.maxValue )
                    cnt++;
                if( targetLayout.progressbarPagesProcessed.value == targetLayout.progressbarPagesProcessed.maxValue )
                    cnt++;
            }
            else
            {
                cnt += 4;   //if no target layout, consider all 4 counts complete
            }
            //console.log( "cnt:" + cnt + " tcnt:" + tcnt + " icnt:" + this.icnt );
        }
        this.icnt++;
        if( cnt >= tcnt && this.icnt >= 5 )     //check counts at least 5 times
            isFinished = true;
        //or just check the total layout counts
        // const targetLayout: any = this.layoutTotal;
        // if( targetLayout.progressbarFoldersSpooled.value == targetLayout.progressbarFoldersSpooled.maxValue )
        //     cnt++;
        // if( targetLayout.progressbarPagesSpooled.value == targetLayout.progressbarPagesSpooled.maxValue )
        //     cnt++;
        // if( targetLayout.progressbarFoldersProcessed.value == targetLayout.progressbarFoldersProcessed.maxValue )
        //     cnt++;
        // if( targetLayout.progressbarPagesProcessed.value == targetLayout.progressbarPagesProcessed.maxValue )
        //     cnt++;
        // this.icnt++;
        // if( cnt >= tcnt && this.icnt >= 5 )
        //     isFinished = true;
        return isFinished;
    }

    private hideShowComponents() {
        this.layoutTotal.visible = this.switchShowAll.checked;
        this.layoutPrint.visible = this.switchShowPrint.checked;
        this.layoutEmail.visible = this.switchShowEmail.checked;
        this.layoutFax.visible = false; //this.switchShowFax.checked;
        this.layoutFTP.visible = this.switchShowFTP.checked;
        if( renlgxLicensed )
            this.layoutLGX.visible = this.switchShowLGX.checked;
    }

/** This is an event handler for the afterExecution event of sourceRenditionBilling. */
    sourceRenditionBillingAfterExecution(event: DataSourceExecutionEvent) {
        const rows: any[] = event.dataSource.data;
        const todaysDate = new Date() //current date
        const tdt: any = todaysDate;
        const tdays = tdt/(24*60*60*1000);  //todaysDate number of days
        //console.log( "today:" + tdays );
        let two=0;
        let week=0;
        let twoweeks=0;
        let tot=0;
        for (const row of rows) {
            //console.log("Row: ", row );
            const dt = DateUtil.parseDate(row._data.renb_billgendt);
            const d: any = dt;
            const days: any = d/(24*60*60*1000);    //billgendt number of days
            //console.log( "billgendt:" + days );
            const diffInDays = tdays - days;
            //console.log( "diffdt:" + diffInDays );
            if( days <= tdays-2 )
                two++;
            if( days <= tdays-7 )
                week++;
            if( days <= tdays-14 )
                twoweeks++;
            tot++;
        }
        this.textSummary.text = "There are [" + two + "] unprocessed folders older than 2 days\n" +
        "There are [" + week + "] unprocessed folders older than 1 week\n" +
        "There are [" + twoweeks + "] unprocessed folders older than 2 weeks\n" +
        "There are [" + tot + "] unprocessed folders total";
    }
}
